import {
  ContentContainer,
  EventImage,
  EventVideo,
  HighlightText,
  ImageContainer,
  Paragraph,
  SectionContainer,
  SectionRoot,
  SocialIcons,
  Title,
} from './styles';
import { Column } from '@components/LinearLayout';
import Button from '@components/Button';
import { useTheme } from 'styled-components';
import { useMobileWidth } from '@utils/hooks';
import Icon from '@components/Icon';
import {
  getFullFacebookHttpLink,
  getFullInstagramHttpLink,
  getFullLinkedInHttpLink,
  getFullTwitterHttpLink,
  getFullXingHttpLink,
} from '@utils/fn';

interface ImageAndTextSectionProps {
  orientation?: 'left' | 'right';
  sectionName: string;
  highlightText?: string;
  titleText: string;
  titleText2?: string;
  paragraphs: string[];
  imageURL?: string;
  buttonText?: string;
  buttonURL?: string;
  hasLogoOverlay?: boolean;
  logoOverlayImgSrc?: string;
  videoUrl?: string;
  maxContentWidth?: string;
}

export const ImageAndTextSection = ({
  orientation = 'left',
  sectionName,
  highlightText,
  buttonText,
  buttonURL,
  titleText,
  titleText2,
  paragraphs,
  videoUrl,
  imageURL,
}: ImageAndTextSectionProps) => {
  const theme = useTheme();
  const isMobile = useMobileWidth();

  const onClickHandler = () => {
    window.location.href = buttonURL ?? '';
  };

  const share = '';
  const linkedin = '';
  const xing = '';
  const twitter = '';
  const facebook = '';

  const socialMediaIcons = [
    {
      icon: 'share',
      onClick: () => window.open(getFullLinkedInHttpLink(share), '_blank'),
    },
    {
      icon: 'social_linkedin',
      onClick: () => window.open(getFullLinkedInHttpLink(linkedin), '_blank'),
    },
    {
      icon: 'social_xing',
      onClick: () => {
        window.open(getFullXingHttpLink(xing), '_blank');
      },
    },
    {
      icon: 'social_twitter',
      onClick: () => window.open(getFullTwitterHttpLink(twitter), '_blank'),
    },
    {
      icon: 'social_facebook',
      onClick: () => window.open(getFullFacebookHttpLink(facebook), '_blank'),
    },
  ];

  return (
    <SectionRoot>
      <SectionContainer orientation={orientation}>
        <ContentContainer>
          <Column $gap="1.5rem">
            <HighlightText>{highlightText}</HighlightText>

            <Title>
              {titleText} {titleText2 && <Title>{titleText2}</Title>}
            </Title>

            <Column $gap="3rem">
              {paragraphs.map((paragraph: string, index: number) => {
                return (
                  <Paragraph key={`paragraph-${sectionName}-${index}`}>
                    {paragraph}
                  </Paragraph>
                );
              })}

              <SocialIcons>
                {socialMediaIcons
                  .filter(item => !!item)
                  .map(item => (
                    <Icon
                      key={item.icon}
                      testId={item.icon}
                      name={item.icon}
                      color={theme.color.black}
                      $hoverColor={theme.color.primary}
                      $clickable
                      onClick={item.onClick}
                      indentLess
                      size={18}
                      $hoverEffect
                    />
                  ))}
              </SocialIcons>

              {buttonText && (
                <Button
                  textColor={theme?.color?.white}
                  backgroundColor={theme?.color?.primary}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  title={buttonText}
                  fontSize={
                    isMobile ? theme.fontSize.size18 : theme.fontSize.size20
                  }
                  width={isMobile ? '100%' : 'fit-content'}
                  padding={isMobile ? '1.715rem 3rem' : '1rem 3rem'}
                  borderRadius={theme.borderRadius.radius5}
                  onClick={onClickHandler}
                />
              )}
            </Column>
          </Column>
        </ContentContainer>

        <ImageContainer>
          {videoUrl ? (
            <EventVideo
              autoPlay={true}
              muted
              loop
              src={videoUrl}
              playsInline
              orientation={orientation}
            />
          ) : (
            <EventImage
              src={imageURL}
              alt={`${sectionName} - ${titleText}`}
              orientation={orientation}
            />
          )}
        </ImageContainer>
      </SectionContainer>
    </SectionRoot>
  );
};
